/**
 * FeedbackButton Component
 * 
 * Trigger button for opening feedback dialog
 */

import React, { useState, useEffect } from 'react';
import { Bug } from 'lucide-react';
import { buttonVariants } from '@/app/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/app/components/ui/tooltip';
import { cn } from '@/app/components/ui/utils';

// Lazy load FeedbackDialog to avoid importing hooks at module level
const FeedbackDialog = React.lazy(() => import('./FeedbackDialog').then(m => ({ default: m.FeedbackDialog })));

export function FeedbackButton() {
  const [isOpen, setIsOpen] = useState(false);
  
  // Global keyboard shortcut: Ctrl+Shift+F
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.shiftKey && event.key === 'F') {
        event.preventDefault();
        setIsOpen(true);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);
  
  return (
    <>
      <TooltipProvider>
        <Tooltip>
          <TooltipTrigger
            onClick={() => setIsOpen(true)}
            className={cn(
              buttonVariants({ variant: 'outline', size: 'sm' }),
              "gap-2 hover:bg-blue-50 hover:border-blue-300 transition-all"
            )}
          >
            <Bug className="w-4 h-4" />
            <span>Feedback</span>
          </TooltipTrigger>
          <TooltipContent>
            <p>Feature Request / Bug melden (Strg+Shift+F)</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
      
      {isOpen && (
        <React.Suspense fallback={null}>
          <FeedbackDialog
            isOpen={isOpen}
            onClose={() => setIsOpen(false)}
          />
        </React.Suspense>
      )}
    </>
  );
}
